import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { Currency } from '@/generated/gql/graphql';

export const demandRequestFormDataSchema = z.object({
  demandRequest: z.object({
    id: z.string().nullable(),
    budget: z.number().positive().nullable(),
    currency: z.enum(Currency),
    summary: z.string().nullable(),
    externalReference: z.string().nullable(),
    commodityGroupId: z.string().nullable(),
    lineItems: z.array(
      z.object({
        id: z.string().nullable(),
        position: z.string(),
        title: z.string().min(1),
        description: z.string().nullable(),
        unitId: z.string().min(1),
        deliveryLocationId: z.string().nullish(),
        organizationId: z.string().nullish(),
        quantity: z.number().positive(),
        shipmentTerms: z.object({
          id: z.string().nullish(),
          earlyDeliveryAt: z.date().nullable(),
          lateDeliveryAt: z.date().nullable(),
        }),
        preferredSupplierId: z.string().nullable(),
        expectedPricePerUnit: z.number().min(0).nullish(),
        baselinePricePerUnit: z.number().min(0).nullish(),
        commodityGroupId: z.string().nullable(),
        commodityId: z.string().nullable(),
        internalId: z.string().nullish(),
        analysisId: z.string().nullish(),
        contractItemId: z.string().nullish(),
      }),
    ),
  }),
});

export type DemandRequestFormData = z.infer<typeof demandRequestFormDataSchema>;

export const demandRequestFormDataDefault: DefaultValues<DemandRequestFormData> = {
  demandRequest: {
    id: null,
    externalReference: null,
    commodityGroupId: null,
    lineItems: [],
    currency: DEFAULT_CURRENCY as Currency,
    budget: null,
  },
};

const TsrctDemandRequestLineItemShipmentTermsMapper = z.object({
  earlyDeliveryAt: z
    .string()
    .transform((val) => (val ? new Date(val as string) : null))
    .nullable(),
  lateDeliveryAt: z
    .string()
    .transform((val) => (val ? new Date(val as string) : null))
    .nullable(),
});

const numberMapper = z
  .string()
  .nullish()
  .transform<number | undefined>((val) => (val ? Number(val) : undefined));

export const TsrctDemandRequestLineItemMapper = z.array(
  z.looseObject({
    quantity: numberMapper,
    expectedPricePerUnit: numberMapper,
    baselinePricePerUnit: numberMapper,
    shipmentTerms: TsrctDemandRequestLineItemShipmentTermsMapper,
  }),
);

export const TsrctDemandRequestMapper = demandRequestFormDataSchema.extend({
  demandRequest: z.looseObject({
    lineItems: TsrctDemandRequestLineItemMapper,
    budget: numberMapper,
  }),
});
